import React, { useContext, useEffect, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import axios from 'axios';
import {
	Typography,
	Button,
	CircularProgress,
	Paper,
	Grid,
	Tabs,
	Tab,
	Box,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	IconButton,
	Tooltip,
} from "@material-ui/core";
import { useHistory } from "react-router-dom";

import { PageContext } from "../../contexts/PageContext";

import AddIcon from "@material-ui/icons/Add";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import GetAppIcon from "@material-ui/icons/GetApp";

import CreditsList from './CreditsList';
import AddCredit from './AddCredit';
import { UserContext } from '../../contexts/UserContext';
import { getTopConditionSettings } from './topConditionsConfig';
import TopConditionsInfo from './TopConditionsInfo';

const useStyles = makeStyles((theme) => ({
	root: {
		maxWidth: 1000,
		marginLeft: "auto",
		marginRight: "auto"
	},
	notification: {
		marginBottom: theme.spacing(2),
		whiteSpace: "pre-line"
	},
	topInfoTabsRow: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
	topInfoTabsRoot: {
		display: "flex",
		backgroundColor: theme.palette.background.paper,
	},
	topInfoTabs: {
		borderRight: `1px solid ${theme.palette.divider}`,
		minWidth: 220,
	},
	topInfoPanel: {
		flex: 1,
		padding: theme.spacing(2),
	},
	topInfoLine: {
		whiteSpace: "pre-line",
	},
	linkTextButton: {
		justifyContent: "flex-start",
		textAlign: "left",
		paddingLeft: 0,
		paddingRight: 0,
		minWidth: 0,
		textTransform: "none",
	},
	headerRow: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		gap: theme.spacing(2),
	},
	actionButtons: theme.actionButtons,
	progress: theme.progress
}));

function TabPanel(props) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`vertical-tabpanel-${index}`}
			aria-labelledby={`vertical-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box>
					{children}
				</Box>
			)}
		</div>
	);
}

function a11yProps(index) {
	return {
		id: `vertical-tab-${index}`,
		'aria-controls': `vertical-tabpanel-${index}`,
	};
}

export default function Credits(props) {
	const theme = useTheme();
	const classes = useStyles();
	const [credits, setCredits] = useState([]);
	const [notification, setNotification] = useState("");
	const [topConditions, setTopConditions] = useState({});
	const [documentInfoFiles, setDocumentInfoFiles] = useState([]);
	const [infoLinks, setInfoLinks] = useState([]);
	const [topInfoTab, setTopInfoTab] = useState(0);
	const [addCreditDialogOpen, setAddCreditDialogOpen] = useState(false);
	const pageContext = useContext(PageContext);
	const userContext = useContext(UserContext);
	let history = useHistory();

	useEffect(() => {
		getCredits();
		getNotification();
		getDocumentInfoFiles();
		getInfoLinks();
		pageContext.setIsLoading(true)
	}, []);


	useEffect(() => {
		getCredits()
		pageContext.setIsLoading(true)
	}, [props.isArchiveView]);

	const getCredits = () => {
		const status = props.isArchiveView ? "successful|archived" : "draft";
		const url = status ? `${process.env.REACT_APP_API_SERVICE_URL}/credits/status/${status}` : `${process.env.REACT_APP_API_SERVICE_URL}/credits`;
		const options = {
			url: url,
			method: 'get',
			headers: userContext.getRequestAuthHeader()
		};
		axios(options)
			.then((res) => {
				setCredits(res.data);
				pageContext.setIsLoading(false);
			})
			.catch((err) => {
				console.log(err);
				pageContext.setIsLoading(false);
			});
	}

	const getNotification = () => {
		const options = {
			url: `${process.env.REACT_APP_API_SERVICE_URL}/settings/string/notification`,
			method: 'get',
			headers: userContext.getRequestAuthHeader()
		};
		axios(options)
			.then(({ data }) => {
				setNotification(data.value);
			})
			.catch((err) => {
				// fail silently
			});
	}

	const role = userContext.user.role;
	const accessToken = userContext.accessToken;
	useEffect(() => {
		// The profile (and role) arrives after the restored access token.
		if (!role || !accessToken) {
			setTopConditions({});
			return;
		}
		let active = true;
		Promise.all(getTopConditionSettings(role).map((setting) => {
			return axios({
				url: `${process.env.REACT_APP_API_SERVICE_URL}/settings/${setting.type}/${setting.key}`,
				method: 'get',
				headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" }
			});
		}))
			.then((responses) => {
				const loadedSettings = {};
				responses.forEach(({ data }) => {
					loadedSettings[data.key] = data;
				});
				if (active) setTopConditions(loadedSettings);
			})
			.catch(() => {
				// fail silently
			});
		return () => { active = false; };
	}, [role, accessToken]);

	const getDocumentInfoFiles = () => {
		const settingsToLoad = [
			"document_info_1",
			"document_info_2",
			"document_info_3",
			"document_info_4",
			"document_info_5",
			"document_info_6",
			"document_info_7",
			"document_info_8",
			"document_info_9",
			"document_info_10",
		];

		Promise.all(settingsToLoad.map((key) => {
			return axios({
				url: `${process.env.REACT_APP_API_SERVICE_URL}/settings/file/${key}`,
				method: 'get',
				headers: userContext.getRequestAuthHeader()
			});
		}))
			.then((responses) => {
				const files = responses
					.map(({ data }) => ({ key: data.key, filename: data.value }))
					.filter((file) => file.filename);
				setDocumentInfoFiles(files);
			})
			.catch(() => {
				// fail silently
			});
	}

	const getInfoLinks = () => {
		const linkIndexes = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

		const settingsToLoad = linkIndexes.flatMap((index) => [
			`info_link_url_${index}`,
			`info_link_description_${index}`,
		]);

		Promise.all(settingsToLoad.map((key) => {
			return axios({
			url: `${process.env.REACT_APP_API_SERVICE_URL}/settings/string/${key}`,
			method: "get",
			headers: userContext.getRequestAuthHeader(),
			});
		}))
			.then((responses) => {
			const loadedSettings = {};
			responses.forEach(({ data }) => {
				loadedSettings[data.key] = data.value;
			});

			const links = linkIndexes
				.map((index) => {
				const url = loadedSettings[`info_link_url_${index}`];
				const description = loadedSettings[`info_link_description_${index}`];

				return {
					key: `info_link_${index}`,
					url,
					description,
				};
				})
				.filter((link) => link.url);

			setInfoLinks(links);
			})
			.catch(() => {
			// fail silently
			});
		};


	// const saveCredit = (editingCredit) => {
	// 	// TODO add authentication
	// 	axios.put(`${process.env.REACT_APP_API_SERVICE_URL}/credits/${editingCredit.id}`, editingCredit)
	// 		.then((res) => {
	// 			getCredits();
	// 			pageContext.setInfoMessage("Finanzierung wurde gespeichert");
	// 		})
	// 		.catch((err) => {
	// 			console.log(err);
	// 			pageContext.setErrorMessage("Finanzierung konnte nicht gespeichert werden!");
	// 		});
	// }

	const deleteCredit = (editingCredit) => {

		console.log(editingCredit)
		pageContext.setConfirmDialog({
			open: true,
			title: "Wirklich löschen?",
			message:
				"Bist du dir sicher, dass du die Finanzierung " +
				(editingCredit.name ? editingCredit.name : "") +
				" löschen möchtest?",
			handleYes: () => {
				pageContext.setConfirmDialog({
					open: false,
				});
				const options = {
					url: `${process.env.REACT_APP_API_SERVICE_URL}/credits/${editingCredit.id}`,
					method: 'delete',
					headers: userContext.getRequestAuthHeader()
				};
				axios(options)
					.then((res) => {
						getCredits();
						pageContext.setInfoMessage("Finanzierung wurde gelöscht");
					})
					.catch((err) => {
						console.log(err);
						pageContext.setErrorMessage("Finanzierung konnte nicht gelöscht werden!");
					});
			},
			handleNo: () => {
				pageContext.setConfirmDialog({
					open: false,
				});
			},
		});
	};

	if (!credits || pageContext.isLoading)
		return <>
			<CircularProgress
				className={classes.progress}
				color="secondary"
			/>
		</>;

	// Wartungsarbeiten
	// return (
	// 	<div className={classes.root}>
	// 		<Paper className={classes.creditRow}>
	// 			<Typography align="center">
	// 				<br /><br />
	// 				Hallo {userContext.user.name},<br />
	// 				Wir führen gerade Wartungsarbeiten durch. Bitte versuche es später noch einmal.
	// 				<br /><br />
	// 			</Typography>
	// 		</Paper>
	// 	</div>
	// );

	const topInfoTabs = [];

	if (notification !== "") {
		topInfoTabs.push({
			label: "Aktuelles",
			content: (
				<Typography className={classes.topInfoLine}>{notification}</Typography>
			),
		});
	}

	topInfoTabs.push({
		label: "Top Zinssätze",
		content: (
			<>
				<Grid container spacing={2} alignItems="flex-start">
					<Grid item>
						<InfoOutlinedIcon color="secondary" />
					</Grid>
					<Grid item xs>
						<Typography variant="subtitle2">Aktuell mögliche Topkonditionen:</Typography>
						<TopConditionsInfo settings={topConditions} role={userContext.user.role} />
					</Grid>
				</Grid>
			</>
		),
	});

	topInfoTabs.push({
		label: "Dokumente",
		content: (
			<>
				<Typography variant="subtitle2">Dokumente zum Download:</Typography>
				{documentInfoFiles.length > 0 ?
					<List dense disablePadding>
						{documentInfoFiles.map((file) => (
							<ListItem 
									key={file.key}
									dense
									disableGutters
									style={{ paddingTop: 0, paddingBottom: 0, marginBottom: -4 }}
								>
								<ListItemIcon>
									<Tooltip title="Download">
										<IconButton
											edge="start"
											aria-label="download"
											download
											target="_blank"
											href={`${process.env.REACT_APP_API_SERVICE_URL}/settings/fileuploads/${file.key}/${file.filename}?token=${userContext.accessToken}`}
										>
											<GetAppIcon />
										</IconButton>
									</Tooltip>
								</ListItemIcon>
								<ListItemText primary={file.filename} />
							</ListItem>
						))}
					</List>
					: <Typography className={classes.topInfoLine}>Keine Dokumente vorhanden.</Typography>
				}
			</>
		),
	});

	const normalizeExternalUrl = (url) => {
		if (!url) return "";

		const trimmed = url.trim();

		if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
			return trimmed;
		}

		return `https://${trimmed}`;
	};

	topInfoTabs.push({
		label: "Links",
		content: (
			<>
			<Typography variant="subtitle2">Weiterführende Links:</Typography>
			{infoLinks.length > 0 ? (
				<List dense disablePadding>
				{infoLinks.map((link) => (
					<ListItem 
						dense
						disableGutters
						style={{ paddingTop: 0, paddingBottom: 0, marginBottom: -4 }}
						key={link.key}>
						<ListItemText
							primary={
							<Button
								color="primary"
								size="small"
								fullWidth
								className={classes.linkTextButton}
								onClick={() => {
								window.open(
									normalizeExternalUrl(link.url),
									"_blank",
									"noopener,noreferrer"
								);
								}}
							>
								{link.description ? link.description.toUpperCase() : link.url}
							</Button>
							}
						/>
            		</ListItem>
				))}
				</List>
			) : (
				<Typography className={classes.topInfoLine}>Keine Links vorhanden.</Typography>
			)}
			</>
		),
	});

	const clampedTopInfoTab = Math.min(topInfoTab, Math.max(topInfoTabs.length - 1, 0));

	return (
		<div className={classes.root}>
			<div className={classes.headerRow}>
				<Typography variant="h2">{props.isArchiveView ? "Archiv" : "Finanzierungen"}</Typography>
				{!props.isArchiveView &&
					<div className={classes.actionButtons}>
						<Button
							variant="contained"
							aria-label="neue Finanzierung anlegen"
							color="primary"
							size="small"
							onClick={() => {
								setAddCreditDialogOpen(true);
							}}
						>
							<AddIcon />
							neue Finanzierung anlegen
						</Button>
					</div>
				}
				{props.isArchiveView && ["administrator", "agent"].includes(userContext.user.role) &&
					<div className={classes.actionButtons}>
						<Button
							variant="contained"
							aria-label="potenzielle Umschuldungen anzeigen"
							color="primary"
							size="small"
							onClick={() => {
								history.push("/potenzielle-umschuldungen/");
							}}
						>
							Potenzielle Umschuldungen anzeigen
						</Button>
					</div>
				}
			</div>
			{!props.isArchiveView && topInfoTabs.length > 0 &&
				<>
					<br />
					<Paper className={classes.topInfoTabsRow}>
						<div className={classes.topInfoTabsRoot}>
							<Tabs
								orientation="vertical"
								variant="scrollable"
								value={clampedTopInfoTab}
								onChange={(event, newValue) => setTopInfoTab(newValue)}
								aria-label="Finanzierungen Info Tabs"
								className={classes.topInfoTabs}
							>
								{topInfoTabs.map((tab, index) => (
									<Tab key={tab.label} label={tab.label} {...a11yProps(index)} />
								))}
							</Tabs>
							<div className={classes.topInfoPanel}>
								{topInfoTabs.map((tab, index) => (
									<TabPanel key={tab.label} value={clampedTopInfoTab} index={index}>
										{tab.content}
									</TabPanel>
								))}
							</div>
						</div>
					</Paper>
				</>
			}
			{/* <Alert severity="warning">WICHTIG! Ab sofort muss jeder Benutzer für sich eigene Zugangsdaten zu den Banken hinterlegen. Falls du das noch nicht gemacht hast, dann <NavLink to="/benutzereinstellungen/">klick bitte hier</NavLink>.</Alert> */}
			<br />
			<CreditsList
				credits={credits}
				getCredits={getCredits}
				deleteCredit={deleteCredit}
				isArchiveView={props.isArchiveView}
			/>
			<AddCredit
				newCreditCallback={
					(newCredit) => {
						console.log("newCreditCallback", newCredit);
						if (userContext.user.role === "tippgeber") {
							history.push("/finanzierungsassistent/" + newCredit.id);
						} else {
							history.push("/finanzierung/" + newCredit.id);
						}
					}
				}
				addCreditDialogOpen={addCreditDialogOpen}
				setAddCreditDialogOpen={setAddCreditDialogOpen}
			/>
		</div>
	);
}
